/**
 * collection-filters.js
 * Self-contained JS for Florin-style collection filters in Dawn theme.
 * Handles: collapsibles, filter-drawer, AJAX filter rendering, price range slider,
 * sort select, active tags, back-button support.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Utility: event delegate helpers (.on / .off like Florin theme)
  ========================================================================== */
  var delegate = {
    on: function (event, callback, options) {
      if (!this.namespaces) this.namespaces = {};
      this.namespaces[event] = callback;
      options = options || false;
      this.addEventListener(event.split('.')[0], callback, options);
      return this;
    },
    off: function (event) {
      if (!this.namespaces) return this;
      this.removeEventListener(event.split('.')[0], this.namespaces[event]);
      delete this.namespaces[event];
      return this;
    }
  };

  if (typeof window !== 'undefined' && typeof Element !== 'undefined') {
    window.on  = window.on  || delegate.on.bind(window);
    window.off = window.off || delegate.off.bind(window);
    Element.prototype.on  = Element.prototype.on  || delegate.on;
    Element.prototype.off = Element.prototype.off || delegate.off;
  }

  /* ==========================================================================
     Utility: debounce
  ========================================================================== */
  function debounce(wait, fn) {
    var timeout;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ==========================================================================
     Utility: prepareTransition (add class → transitionend → remove)
  ========================================================================== */
  function prepareTransition(el, fn) {
    el.addEventListener('transitionend', function done() {
      el.removeEventListener('transitionend', done);
    });
    fn();
  }

  /* ==========================================================================
     AjaxRenderer
  ========================================================================== */
  function AjaxRenderer(opts) {
    opts = opts || {};
    this.sections      = opts.sections || [];
    this.cachedSections = [];
    this.onReplace     = opts.onReplace;
  }

  AjaxRenderer.prototype.renderPage = function (basePath, newParams, updateURLHash) {
    if (updateURLHash === undefined) updateURLHash = true;

    var currentParams = new URLSearchParams(window.location.search);
    var updatedParams = this._getUpdatedParams(currentParams, newParams);

    var sectionRenders = this.sections.map(function (section) {
      var url = basePath + '?section_id=' + section.sectionId + '&' + updatedParams.toString();
      var cached = this.cachedSections.find(function (s) { return s.url === url; });
      return cached
        ? this._renderSectionFromCache(cached, section)
        : this._renderSectionFromFetch(url, section);
    }, this);

    if (updateURLHash) {
      window.history.pushState({}, '', basePath + '?' + updatedParams.toString());
    }

    return Promise.all(sectionRenders);
  };

  AjaxRenderer.prototype._getUpdatedParams = function (currentParams, newParams) {
    var updatedParams = new URLSearchParams(currentParams.toString());
    newParams.forEach(function (value, key) {
      if (!value) {
        updatedParams.delete(key);
      } else {
        updatedParams.set(key, value);
      }
    });
    return updatedParams;
  };

  AjaxRenderer.prototype._renderSectionFromCache = function (cached, section) {
    this._renderSection(cached.html, section);
    return Promise.resolve(section);
  };

  AjaxRenderer.prototype._renderSectionFromFetch = function (url, section) {
    var self = this;
    return new Promise(function (resolve, reject) {
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          self.cachedSections.push({ html: html, url: url });
          self._renderSection(html, section);
          resolve(section);
        })
        .catch(reject);
    });
  };

  AjaxRenderer.prototype._renderSection = function (html, section) {
    var newDom = new DOMParser().parseFromString(html, 'text/html');
    if (this.onReplace) {
      this.onReplace(newDom, section);
    } else {
      var newContentEl = newDom.getElementById(section.nodeId);
      var existingEl   = document.getElementById(section.nodeId);
      if (newContentEl && existingEl) {
        existingEl.innerHTML = newContentEl.innerHTML;
      }
    }
  };

  /* ==========================================================================
     Collapsibles
  ========================================================================== */
  var Collapsibles = (function () {
    var isTransitioning = false;

    function init(scope) {
      var root = scope || document;
      root.querySelectorAll('.collapsible-trigger').forEach(function (trigger) {
        var state = trigger.classList.contains('is-open');
        trigger.setAttribute('aria-expanded', state);
        // Remove old listener then rebind
        trigger.removeEventListener('click', trigger._collapsibleClick);
        trigger._collapsibleClick = function (evt) { toggle(evt); };
        trigger.addEventListener('click', trigger._collapsibleClick);
      });
    }

    function toggle(evt) {
      if (isTransitioning) return;
      isTransitioning = true;

      var el     = evt.currentTarget;
      var isOpen = el.classList.contains('is-open');
      // aria-controls holds the full ID of the content panel
      var moduleId  = el.getAttribute('aria-controls');
      var container = moduleId ? document.getElementById(moduleId) : null;

      // Fallback: use data-collapsible-id to find sibling content
      if (!container) {
        var collapsibleId = el.dataset.collapsibleId;
        if (collapsibleId) {
          var parent = el.parentNode;
          container  = parent ? parent.querySelector('[data-collapsible-id="' + collapsibleId + '"].collapsible-content') : null;
        }
      }

      if (!container) { isTransitioning = false; return; }

      var isAutoHeight = el.classList.contains('collapsible--auto-height');

      if (isOpen) {
        el.classList.remove('is-open');
        el.setAttribute('aria-expanded', 'false');
        container.classList.remove('is-open');
        if (isAutoHeight) {
          container.style.height = container.scrollHeight + 'px';
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { container.style.height = '0px'; });
          });
        } else {
          container.style.height = '0px';
        }
      } else {
        el.classList.add('is-open');
        el.setAttribute('aria-expanded', 'true');
        container.classList.add('is-open');
        if (isAutoHeight) {
          container.style.height = 'auto';
        } else {
          container.style.height = container.scrollHeight + 'px';
        }
      }

      setTimeout(function () { isTransitioning = false; }, 400);
    }

    return { init: init };
  })();

  /* ==========================================================================
     Drawer
  ========================================================================== */
  function Drawer(id, name) {
    this.config = {
      id:           id,
      closeClass:   '.js-drawer-close',
      openClass:    '.js-drawer-open-' + name,
      activeClass:  'drawer--is-open',
      openBodyClass:'js-drawer-open',
      namespace:    '.drawer-' + name
    };
    this.drawer  = document.querySelector('#' + id);
    this.isOpen  = false;
    if (!this.drawer) return;
    this._init();
  }

  Drawer.prototype._init = function () {
    var self = this;
    document.querySelectorAll(this.config.openClass).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function (e) { self.open(e); });
    });
    var closeBtn = this.drawer.querySelector(this.config.closeClass);
    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

    // Close on overlay click
    this.drawer.addEventListener('click', function (e) {
      if (e.target === self.drawer) self.close();
    });
  };

  Drawer.prototype.open = function (evt) {
    if (evt) { evt.preventDefault(); evt.stopPropagation(); }
    if (this.isOpen) return;
    if (!this.drawer) return;
    this.drawer.classList.add(this.config.activeClass);
    document.documentElement.classList.add(this.config.openBodyClass);
    this.isOpen = true;
    document.querySelectorAll(this.config.openClass).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
  };

  Drawer.prototype.close = function () {
    if (!this.isOpen) return;
    if (!this.drawer) return;
    var self = this;
    prepareTransition(this.drawer, function () {
      self.drawer.classList.remove(self.config.activeClass);
    });
    document.documentElement.classList.remove(this.config.openBodyClass);
    this.isOpen = false;
    document.querySelectorAll(this.config.openClass).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  };

  /* ==========================================================================
     Price Range (vanilla — no noUiSlider dependency)
     Uses two range inputs overlaid to create a dual-handle slider.
  ========================================================================== */
  function PriceRange(container, opts) {
    this.container   = container;
    this.onChange    = opts && opts.onChange;
    this.init();
  }

  PriceRange.prototype.init = function () {
    this.formEl      = this.container.closest('form');
    this.inputMinEl  = this.container.querySelector('.price-range__input-min');
    this.inputMaxEl  = this.container.querySelector('.price-range__input-max');
    this.displayMinEl = this.container.querySelector('.price-range__display-min');
    this.displayMaxEl = this.container.querySelector('.price-range__display-max');
    this.sliderEl    = this.container.querySelector('.price-range__slider');

var rawMinRange = parseFloat(this.container.dataset.min);
var rawMinValue = parseFloat(this.container.dataset.minValue);
var rawMaxRange = parseFloat(this.container.dataset.max);
var rawMaxValue = parseFloat(this.container.dataset.maxValue);

this.minRange = isNaN(rawMinRange) ? 0 : rawMinRange;
this.maxRange = isNaN(rawMaxRange) ? 0 : rawMaxRange;

this.minValue = isNaN(rawMinValue)
  ? this.minRange
  : rawMinValue;

this.maxValue = isNaN(rawMaxValue)
  ? this.maxRange
  : rawMaxValue;

/*
 * Shopify can return the active price value
 * in a different representation after AJAX.
 *
 * Keep everything in the same unit as range_max.
 */
if (this.maxValue > this.maxRange && this.maxRange > 0) {
  this.maxValue = this.maxValue / 100;
}

if (this.minValue > this.maxRange && this.maxRange > 0) {
  this.minValue = this.minValue / 100;
}

/*
 * Never allow values outside the slider range.
 */
this.minValue = Math.max(
  this.minRange,
  Math.min(this.minValue, this.maxRange)
);

this.maxValue = Math.max(
  this.minRange,
  Math.min(this.maxValue, this.maxRange)
);

    this._buildSlider();
    this._updateDisplay(this.minValue, this.maxValue);
  };

PriceRange.prototype._buildSlider = function () {
  if (!this.sliderEl) return;

  var self = this;

  var minRange = this.minRange;
  var maxRange = this.maxRange;

  /* Hide the real Shopify filter inputs */
  if (this.inputMinEl) {
    this.inputMinEl.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
  }

  if (this.inputMaxEl) {
    this.inputMaxEl.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
  }

  /* Main slider wrapper */
  var sliderWrap = document.createElement('div');
  sliderWrap.className = 'pr-slider-wrap';

  sliderWrap.style.cssText =
    'position:relative;width:100%;height:24px;margin:4px 0 8px;touch-action:none;';

  /* Background track */
  var track = document.createElement('div');
  track.className = 'pr-track';

  track.style.cssText =
    'position:absolute;left:0;right:0;top:50%;height:3px;background:#d0d0d0;transform:translateY(-50%);border-radius:3px;';

  /* Active track */
  var fill = document.createElement('div');
  fill.className = 'pr-fill';

  fill.style.cssText =
    'position:absolute;top:50%;height:3px;background:#111;transform:translateY(-50%);border-radius:3px;pointer-events:none;';

  /* Minimum handle */
  var handleMin = document.createElement('div');
  handleMin.className = 'pr-handle pr-handle--min';

  handleMin.style.cssText =
    'position:absolute;top:50%;width:12px;height:12px;background:#111;border-radius:50%;transform:translate(-50%,-50%);cursor:grab;z-index:2;pointer-events:none;';

  /* Maximum handle */
  var handleMax = document.createElement('div');
  handleMax.className = 'pr-handle pr-handle--max';

  handleMax.style.cssText =
    'position:absolute;top:50%;width:12px;height:12px;background:#111;border-radius:50%;transform:translate(-50%,-50%);cursor:grab;z-index:2;pointer-events:none;';

  /*
   * One transparent pointer input.
   * This is the actual clickable/drag area.
   */
  var rangeInput = document.createElement('input');

  rangeInput.type = 'range';
  rangeInput.min = minRange;
  rangeInput.max = maxRange;
  rangeInput.step = 1;
  rangeInput.value = this.minValue;

  rangeInput.className = 'pr-range';

  rangeInput.style.cssText =
    'position:absolute;left:0;top:0;width:100%;height:100%;margin:0;padding:0;opacity:0;cursor:pointer;z-index:10;-webkit-appearance:none;appearance:none;background:transparent;';

  sliderWrap.appendChild(track);
  sliderWrap.appendChild(fill);
  sliderWrap.appendChild(handleMin);
  sliderWrap.appendChild(handleMax);
  sliderWrap.appendChild(rangeInput);

  this.sliderEl.innerHTML = '';
  this.sliderEl.appendChild(sliderWrap);

  this._fill = fill;
  this._handleMin = handleMin;
  this._handleMax = handleMax;
  this._rangeInput = rangeInput;

  var activeHandle = 'min';

  function getPercentage(value) {
    if (maxRange === minRange) return 0;

    return (
      ((value - minRange) /
        (maxRange - minRange)) *
      100
    );
  }

  function getValueFromPointer(clientX) {
    var rect = sliderWrap.getBoundingClientRect();

    var percent =
      (clientX - rect.left) / rect.width;

    percent = Math.max(0, Math.min(1, percent));

    return Math.round(
      minRange +
      percent * (maxRange - minRange)
    );
  }

  function updateUI() {
    var minValue = parseFloat(self.minValue);
    var maxValue = parseFloat(self.maxValue);

    var minPercent = getPercentage(minValue);
    var maxPercent = getPercentage(maxValue);

    fill.style.left = minPercent + '%';
    fill.style.width =
      (maxPercent - minPercent) + '%';

    handleMin.style.left =
      minPercent + '%';

    handleMax.style.left =
      maxPercent + '%';

    rangeInput.value = minValue;
  }

  function updateDisplay() {
    self._updateDisplay(
      self.minValue,
      self.maxValue
    );

    updateUI();
  }

  function beginDrag(evt) {
    evt.preventDefault();

    var value = getValueFromPointer(evt.clientX);

    var minValue = parseFloat(self.minValue);
    var maxValue = parseFloat(self.maxValue);

    /*
     * Decide which handle is closer
     */
    var minDistance =
      Math.abs(value - minValue);

    var maxDistance =
      Math.abs(value - maxValue);

    activeHandle =
      minDistance <= maxDistance
        ? 'min'
        : 'max';

    if (activeHandle === 'min') {
      self.minValue =
        Math.min(value, maxValue);
    } else {
      self.maxValue =
        Math.max(value, minValue);
    }

    updateDisplay();

    function onMove(moveEvt) {
      moveEvt.preventDefault();

      var newValue =
        getValueFromPointer(
          moveEvt.clientX
        );

      var currentMin =
        parseFloat(self.minValue);

      var currentMax =
        parseFloat(self.maxValue);

      if (activeHandle === 'min') {
        self.minValue =
          Math.min(newValue, currentMax);
      } else {
        self.maxValue =
          Math.max(newValue, currentMin);
      }

      updateDisplay();
    }

    function onUp() {
      document.removeEventListener(
        'pointermove',
        onMove
      );

      document.removeEventListener(
        'pointerup',
        onUp
      );

      /*
       * Write values into Shopify's real
       * filter inputs.
       */
      if (self.inputMinEl) {
        self.inputMinEl.value =
          self.minValue;
      }

      if (self.inputMaxEl) {
        self.inputMaxEl.value =
          self.maxValue;
      }

      /*
       * Trigger Shopify AJAX filtering
       */
      if (self.onChange && self.formEl) {
        self.onChange(
          new FormData(self.formEl)
        );
      }
    }

    document.addEventListener(
      'pointermove',
      onMove,
      { passive: false }
    );

    document.addEventListener(
      'pointerup',
      onUp
    );
  }

  /*
   * Listen directly on the transparent input.
   */
  rangeInput.addEventListener(
    'pointerdown',
    beginDrag
  );

  updateUI();

  self._updateDisplay(
    self.minValue,
    self.maxValue
  );
};

PriceRange.prototype._updateDisplay = function (min, max) {
  var moneyFormat =
    (window.theme &&
      window.theme.settings &&
      window.theme.settings.moneyFormat) ||
    (window.Shopify &&
      window.Shopify.money_format) ||
    '${{amount}}';

  var fmt = function (val) {
    var amount = (val / 100).toFixed(2);

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/g,
        Math.round(val / 100)
      )
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        amount.replace('.', ',')
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        Math.round(val / 100)
      );
  };

  if (this.displayMinEl) {
    this.displayMinEl.innerHTML = fmt(min);
  }

  if (this.displayMaxEl) {
    this.displayMaxEl.innerHTML = fmt(max);
  }

};
  /* ==========================================================================
     Collection Controller
  ========================================================================== */
  var CollectionController = (function () {
   var isAnimating = false;
  var FilterDrawer = null;  
  var drawerWasOpen = false;

    function init(container) {
      if (!container) return;

      var sectionId = container.getAttribute('data-section-id');
      if (!sectionId) return;

      // Init collapsibles
      Collapsibles.init(container);

      // Init filter drawer
      FilterDrawer = new Drawer(
  'FilterDrawer',
  'collection-filters'
);

      // Ajax renderer
      var ajaxRenderer = new AjaxRenderer({
        sections: [{ sectionId: sectionId, nodeId: 'CollectionAjaxContent' }],
        onReplace: function (newDom, section) {
          // Re-open any collapsibles that were open before re-render
          var newContentEl = newDom.getElementById(section.nodeId);
          var existingEl   = document.getElementById(section.nodeId);
          if (newContentEl && existingEl) {
            existingEl.innerHTML = newContentEl.innerHTML;
          }
        }
      });

      // Helper: re-init after AJAX
function afterRender() {
  Collapsibles.init(container);

  FilterDrawer = new Drawer(
    'FilterDrawer',
    'collection-filters'
  );

  initPriceRanges(container, renderFromFormData);
  initFilters(container);

  // Re-open drawer if it was open before filtering
  if (drawerWasOpen && FilterDrawer) {
    FilterDrawer.open();
  }

  isAnimating = false;
}
      // Sort select
      var sortSelect = document.querySelector('#SortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', function () {
          var params = new URLSearchParams(window.location.search);
          params.set('sort_by', sortSelect.value);
          params.delete('page');
          ajaxRenderer.renderPage(window.location.pathname, params).then(afterRender);
        });
      }

      // -----------------------------------------------------------------------
      // Active tag removal links
      // -----------------------------------------------------------------------
      function delegateTagClick(evt) {
        var el = evt.target.closest('.tag--remove a');
        if (!el) return;
        if (el.classList.contains('no-ajax')) return;
        evt.preventDefault();
        if (isAnimating) return;
        isAnimating = true;
        if (FilterDrawer) FilterDrawer.close();
        var newUrl = new URL(el.href);
        var li = el.closest('li');
        if (li) li.remove();
        ajaxRenderer.renderPage(newUrl.pathname, newUrl.searchParams).then(afterRender);
      }
      document.addEventListener('click', delegateTagClick);

      // -----------------------------------------------------------------------
      // Filter form inputs
      // -----------------------------------------------------------------------
function initFilters(scope) {
  var root = scope || document;

  root.querySelectorAll('.filter-form').forEach(function (form) {
    form.removeEventListener('input', form._filterInputHandler);

    form._filterInputHandler = function (evt) {
      var el = evt.target;

      if (el.classList.contains('no-ajax')) return;
      if (el.type === 'range') return;

      evt.preventDefault();

   if (isAnimating) return;
isAnimating = true;

// Remember whether drawer was open before AJAX replacement
drawerWasOpen = FilterDrawer && FilterDrawer.isOpen;

var parent = el.closest('li');

      var parent = el.closest('li');

      if (parent) {
        if (parent.classList.contains('tag--active')) {
          parent.classList.remove('tag--active');
        } else {
          parent.classList.add('tag--active');
        }
      }

      renderFromFormData(new FormData(form));
    };

    form.addEventListener('input', form._filterInputHandler);
  });
}
      initFilters(container);

      // -----------------------------------------------------------------------
      // Render from FormData
      // -----------------------------------------------------------------------
      function renderFromFormData(formData) {
        var searchParams = new URLSearchParams(formData);
        ajaxRenderer.renderPage(window.location.pathname, searchParams).then(afterRender);
      }

      // -----------------------------------------------------------------------
      // Price ranges
      // -----------------------------------------------------------------------
      function initPriceRanges(scope, onChange) {
        var root = scope || document;
        root.querySelectorAll('.price-range').forEach(function (el) {
          if (el._priceRangeInit) return;
          el._priceRangeInit = true;
          new PriceRange(el, { onChange: onChange });
        });
      }
      initPriceRanges(container, renderFromFormData);

      // -----------------------------------------------------------------------
      // Back button support
      // -----------------------------------------------------------------------
      window.addEventListener('popstate', function (evt) {
        if (evt.state) {
          var newUrl = new URL(window.location.href);
          ajaxRenderer.renderPage(newUrl.pathname, newUrl.searchParams, false).then(afterRender);
        }
      });
    }

    return { init: init };
  })();

  /* ==========================================================================
     Boot — initialise when DOM is ready
  ========================================================================== */
  function boot() {
    var container = document.querySelector('[data-section-type="collection-grid"]');
    if (container) {
      CollectionController.init(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Also expose for Shopify section re-init
  window.CollectionFilters = { boot: boot };
})();
